import { Link } from "react-router-dom";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { SeoHead } from "@/components/SeoHead";
import { WebPageJsonLd } from "@/components/marketing/WebPageJsonLd";

const EFFECTIVE_DATE = "June 23, 2026";

export default function LegalCitationStyles() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Citation Styles Attribution — Excerptus"
        description="Excerptus renders citations using the Citation Style Language (CSL) repository, licensed under CC BY-SA 3.0."
      />
      <WebPageJsonLd
        name="Citation Styles Attribution — Excerptus"
        description="Attribution and licensing for the Citation Style Language styles used in Excerptus."
        path="/legal/citation-styles/"
        dateModified="2026-06-23"
      />
      <MarketingNav />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <article className="prose-legal">
          <h1 className="font-serif text-4xl font-semibold text-foreground">Citation Styles Attribution</h1>
          <p className="mt-2 text-sm text-muted-foreground">Effective: {EFFECTIVE_DATE}</p>

          <Section title="1. What this page is">
            Excerptus formats citations and bibliographies using the open-source{" "}
            <a className="text-primary underline" href="https://citationstyles.org/" target="_blank" rel="noreferrer">
              Citation Style Language
            </a>{" "}
            (CSL) and the community-maintained{" "}
            <a className="text-primary underline" href="https://github.com/citation-style-language/styles" target="_blank" rel="noreferrer">
              CSL Style Repository
            </a>. This page provides the attribution required by the styles' license.
          </Section>

          <Section title="2. License">
            The CSL styles and locale files used by Excerptus are licensed under the{" "}
            <a className="text-primary underline" href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank" rel="noreferrer">
              Creative Commons Attribution-ShareAlike 3.0 Unported License
            </a>{" "}
            (CC BY-SA 3.0). Individual styles credit their authors in the file's metadata. Excerptus
            serves these styles unmodified from a quarterly snapshot of the upstream repository.
          </Section>

          <Section title="3. The processor">
            Citations are rendered in your browser by{" "}
            <a className="text-primary underline" href="https://github.com/Juris-M/citeproc-js" target="_blank" rel="noreferrer">
              citeproc-js
            </a>{" "}
            (© Frank Bennett, distributed under the Common Public Attribution License Version 1.0 / AGPL v3 /
            ALv2 multi-license).
          </Section>

          <Section title="4. ShareAlike notice">
            Because the styles are licensed under CC BY-SA 3.0, any derivative styles must be distributed
            under the same license. Excerptus does not modify the upstream style files; we serve them as-is.
            If you author a custom CSL style and want it included, contact us — derivative works contributed
            back will be offered to the upstream repository under the same license.
          </Section>

          <Section title="5. Updates">
            The style snapshot in Excerptus is refreshed on a roughly quarterly cadence. Between refreshes,
            individual styles in the upstream repository may have moved ahead of the version rendered here.
          </Section>

          <Section title="6. Contact">
            Questions about citation rendering or attribution? Email{" "}
            <a className="text-primary underline" href="mailto:accounts@excerptus.io">
              accounts@excerptus.io
            </a>
            . Privacy and cookie policies are linked from the{" "}
            <Link className="text-primary underline" to="/privacy/">Privacy</Link> and{" "}
            <Link className="text-primary underline" to="/cookies/">Cookie</Link> pages.
          </Section>
        </article>
      </main>
      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-serif text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-2 text-base leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}
